<template>
    <div class="grid-container md:p-4">
        <PlanLockOverlay v-for="(student, index) in students" :key="index" :locked="!!student.lockedByPlan"
            message="Locked — this student is beyond your plan's enrolled student limit. Classes are paused and will not be charged until you upgrade or end another enrolment.">
            <Card class="Card p-4 h-full">
                <template #content>
                    <div class="flex flex-column align-items-start justify-content-start gap-2 text-sm">
                        <div class="flex justify-content-start gap-2 ">
                            <Avatar
                                :image="!student.studentPhotoURL || student.studentPhotoURL == 'Service is not available in local environment' ? blankProfile : profileImageUrl(student.studentPhotoURL)"
                                alt="Image" class="mr-2 flex-shrink-0" size="xlarge" shape="circle" />
                            <div class="flex flex-column justify-content-center gap-2 pb-2 ">
                                <span class="text-left Paragraph-Medium -mb-2 student-name">{{ student.studentName }}</span>
                                <span class="Caption">{{ cleanupLables(student.grade) }}</span>
                            </div>
                        </div>
                        <div class="flex justify-content-start gap-2">
                            <span class="Paragraph-Regular"> {{ student.classModes.join(' | ').replace('Class','')}}</span>
                        </div>
                        <div class="flex justify-content-start gap-2 pb-2 ">
                            <span class="Paragraph-Small"> {{ formatClassModels(student.classModels) }}</span>
                        </div>
                        <div class="flex flex-wrap align-items-center justify-content-start gap-2 ">
                            <span v-for="subject in student.subjects" :key="subject"
                                class="flex items-center subjects justify-center px-2 py-2  Caption">
                                {{ cleanupLables(subject) }}</span>
                        </div>
                        <div class="flex justify-content-start gap-2 pt-2">
                            <span class="Caption">Next class date: {{ student.nextClassDate }}</span>
                        </div>
                    </div>
                </template>
                <template #footer>
                    <div class="flex justify-content-center align-items-center gap-2 mt-3">
                        <div style="width: 30px;"></div>
                        <Button label="View" class="teal-button-small" @click="$emit('view-student', student)" />
                        <ChatButton :otherUserId="student.studentId" :otherUserName="student.studentName" @open-chat="$emit('open-chat', $event)" />
                    </div>
                </template>
            </Card>
        </PlanLockOverlay>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref } from "vue";
import Card from "primevue/card";
import Button from "primevue/button";
import Avatar from "primevue/avatar";
import PlanLockOverlay from "@/components/PlanLockOverlay.vue";
import blankProfile from '@/assets/blank-profile.png';
import { cleanupLables } from "@/utils/cleanups";
import { generateSignedUrl } from '@/api/storage';
import ChatButton from "@/components/ChatButton.vue";

defineProps({
    students: {
        type: Array,
        required: true
    },
});

defineEmits(['view-student', 'open-chat']);
const imageUrls = ref({});

const profileImageUrl = (filePath) => {
    if (!imageUrls.value[filePath]) {
        generateSignedUrl(filePath)
            .then(url => {
                imageUrls.value[filePath] = url;
            })
            .catch(() => {
                imageUrls.value[filePath] = blankProfile;
            });
    }
    return imageUrls.value[filePath];
};

const formatClassModels = (classModels) => {
    if (!classModels) return ''; 
    
    const hasIndividual = classModels.includes('Individual Class');
    const hasGroup = classModels.includes('Group Class');

    if (hasIndividual && hasGroup) {
        return 'Individual & Group Classes';
    } else if (hasIndividual) {
        return 'Individual Class';
    } else if (hasGroup) {
        return 'Group Class';
    }
    return classModels.join(' | ');
};

// function convertISOToLocalReadable(isoString) {
//     if (!isoString) return 'N/A';
    
//     const date = new Date(isoString);
//     return date.toLocaleString('en-US', {
//         year: 'numeric',
//         month: 'long',
//         day: 'numeric',
//         hour: '2-digit',
//         minute: '2-digit',
//         second: '2-digit',
//         hour12: true, 
//     });
// }
</script>

<style scoped>
.grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
    gap: 3rem;
}

.Card {
    border-radius: 4px;
    box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.16);
    height: 100%;
}

.Card :deep(.p-card-body) {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
}

.Card :deep(.p-card-content) {
    flex-grow: 1;
    padding-bottom: 0;
}

.Card :deep(.p-card-footer) {
    margin-top: auto;
    padding-top: 1rem;
}

.p-button-danger {
    padding: 10px 16px 10px 16px;
    height: 30px;
}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}

.subjects {
    border: 1px solid var(--gray-700) !important;
    border-radius: 50px;
    background-color: transparent;
}

.student-name {
    overflow-wrap: anywhere;
    word-break: break-word;
}

.flex-shrink-0 {
    flex-shrink: 0;
}

.Card :deep(.p-avatar.p-avatar-xl) {
    width: 4rem;
    height: 4rem;
    flex-shrink: 0;
}

.Card :deep(.p-avatar.p-avatar-xl img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
</style>